import React, { useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DashboardLayout from "../Layout/DashboardLayout";
import DashboardTopbar from "../Layout/DashboardTopbar";
import useTokenBalance from "../hooks/useTokenBalance";
import { useApiGenerationContext } from "../Context/ApiGenerationContext";
import {
  API_BASE_URL,
  API_CREATE_MODEL_URI,
  API_DELETE_MODEL_URI,
  API_GET_MODELS_URI,
  API_UPDATE_MODEL_URI
} from "../Context/Constants";
import "./ApiView/api-view.css";

const ApiView = () => {
  const { t } = useTranslation("common");
  const { tokenBalance } = useTokenBalance();
  const [currentTab, setCurrentTab] = useState("api");
  const [copyFeedback, setCopyFeedback] = useState("");
  const {
    selectedConfig,
    currentModel,
    selectedFields,
    selectedMethod,
    fieldType,
    requiredFields,
    filterFields,
    selectedRecord,
    completeQuery,
    odooEngineVersion
  } = useApiGenerationContext();

  const apiDetails = useMemo(() => {
    if (!selectedMethod) return {};
    const p = {
      action: selectedMethod,
      model: currentModel,
      Engine_version: odooEngineVersion
    };
    if (selectedMethod === "create") {
      p.input = selectedFields?.reduce((prev, f) => ({ ...prev, [f]: t("YOUR_VALUE") }), {});
    } else if (selectedMethod === "delete") {
      p.id = selectedRecord ? selectedRecord : t("YOUR_RECORD_ID");
    } else if (selectedMethod === "update") {
      p.input = selectedFields?.reduce((prev, f) => ({ ...prev, [f]: t("YOUR_VALUE") }), {});
      p.id = selectedRecord ? selectedRecord : t("YOUR_RECORD_ID");
    } else if (selectedMethod === "view") {
      p.fields = completeQuery;
      p.outputrecords = {
        fields: selectedFields
      };
    }
    return p;
  }, [currentModel, selectedMethod, selectedFields, selectedRecord, completeQuery, odooEngineVersion, t]);

  const uriMap = {
    create: API_CREATE_MODEL_URI,
    update: API_UPDATE_MODEL_URI,
    delete: API_DELETE_MODEL_URI,
    view: API_GET_MODELS_URI
  };

  const endpoint = useMemo(() => {
    if (!selectedMethod) return "";
    return API_BASE_URL + uriMap[selectedMethod];
  }, [selectedMethod]);

  const METHOD = "POST";

  const payload = useMemo(() => {
    if (!selectedMethod) return {};
    const p = {
      configName: selectedConfig,
      model: currentModel,
      Engine_version: odooEngineVersion
    };
    if (selectedMethod === "create") {
      p.serviceValidation = true;
      p.required = requiredFields;
      p.inputFields = selectedFields;
      p.properties = fieldType;
      p.input = selectedFields?.reduce((prev, f) => ({ ...prev, [f]: t("YOUR_VALUE") }), {});
    } else if (selectedMethod === "delete") {
      p.id = selectedRecord ? selectedRecord : t("YOUR_RECORD_ID");
    } else if (selectedMethod === "update") {
      p.serviceValidation = true;
      p.required = requiredFields;
      p.inputFields = selectedFields;
      p.properties = fieldType;
      p.input = selectedFields?.reduce((prev, f) => ({ ...prev, [f]: t("YOUR_VALUE") }), {});
      p.id = selectedRecord ? selectedRecord : t("YOUR_RECORD_ID");
    } else if (selectedMethod === "view") {
      p.properties = fieldType;
      p.fields = completeQuery;
      p.outputrecords = { fields: selectedFields };
      p.id = selectedRecord ? selectedRecord : t("YOUR_RECORD_ID");
    }
    return p;
  }, [
    selectedMethod,
    selectedFields,
    fieldType,
    requiredFields,
    selectedConfig,
    currentModel,
    filterFields,
    selectedRecord,
    completeQuery,
    odooEngineVersion,
    t
  ]);

  const headers = useMemo(
    () => ({
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: t("YOUR_ID_TOKEN")
    }),
    [t]
  );

  const apiDetailsJson = useMemo(() => JSON.stringify(apiDetails, null, 2), [apiDetails]);
  const headersJson = useMemo(() => JSON.stringify(headers, null, 2), [headers]);
  const payloadJson = useMemo(() => JSON.stringify(payload, null, 2), [payload]);

  const copyText = (text, feedbackKey) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
      setCopyFeedback(feedbackKey);
      setTimeout(() => setCopyFeedback(""), 1500);
    }
  };

  const copyAll = () => {
    const all = `${apiDetailsJson}\n\n${endpoint}\n\n${headersJson}\n\n${payloadJson}`;
    copyText(all, "all");
  };

  if (!selectedMethod) {
    return <Navigate to="/api-generator" />;
  }

  return (
    <DashboardLayout>
      <div className="dashboard-page">
        <DashboardTopbar title={t("View Api")} tokenBalance={tokenBalance} />

        <div className="page-content">
          <div className="view-toolbar">
            <Link to="/api-generator" className="btn-back">
              ← {t("Go Back")}
            </Link>
            <Link to="/api-generator/form" replace className="btn-test">
              🧪 {t("Test Api")}
            </Link>
            <button type="button" className="copy-btn" onClick={copyAll}>
              {copyFeedback === "all" ? t("All Copied!") : `📋 ${t("Copy All")}`}
            </button>
          </div>

          <div className="api-tabs">
            <button
              type="button"
              className={`api-tab${currentTab === "api" ? " active" : ""}`}
              onClick={() => setCurrentTab("api")}>
              {t("API Syntax")}
            </button>
            <button
              type="button"
              className={`api-tab${currentTab === "web_api" ? " active" : ""}`}
              onClick={() => setCurrentTab("web_api")}>
              {t("Web API")}
            </button>
          </div>

          {currentTab === "api" ? (
            <div>
              <div className="api-syntax-header">
                <span className="syntax-mode-label">{t("Full Syntax Mode")}</span>
                <span className="json-badge">JSON</span>
                <button
                  type="button"
                  className="copy-btn ml-auto"
                  onClick={() => copyText(apiDetailsJson, "api")}>
                  {copyFeedback === "api" ? t("Copied!") : `📋 ${t("Copy")}`}
                </button>
              </div>
              <pre className="code-block">{apiDetailsJson}</pre>
            </div>
          ) : (
            <div>
              <div className="webapi-section">
                <div className="webapi-label">{t("Endpoint")}</div>
                <div className="webapi-endpoint">{endpoint}</div>
              </div>

              <div className="webapi-section">
                <div className="webapi-label">{t("Method")}</div>
                <div className="webapi-method">{METHOD}</div>
              </div>

              <div className="webapi-section">
                <div className="webapi-section-header">
                  <div className="webapi-label">{t("Headers")}</div>
                  <button
                    type="button"
                    className="copy-btn ml-auto"
                    onClick={() => copyText(headersJson, "headers")}>
                    {copyFeedback === "headers" ? t("Copied!") : `📋 ${t("Copy")}`}
                  </button>
                </div>
                <pre className="code-block">{headersJson}</pre>
              </div>

              <div className="webapi-section">
                <div className="webapi-section-header">
                  <div className="webapi-label">{t("Payload")}</div>
                  <button
                    type="button"
                    className="copy-btn ml-auto"
                    onClick={() => copyText(payloadJson, "payload")}>
                    {copyFeedback === "payload" ? t("Copied!") : `📋 ${t("Copy")}`}
                  </button>
                </div>
                <pre className="code-block">{payloadJson}</pre>
              </div>

              <div className="quick-use-grid">
                <div className="quick-use-card">
                  <div className="quick-use-label">cURL</div>
                  <code className="quick-use-code curl">
                    curl -X POST {"\n"}  -H &quot;Authorization: TOKEN&quot; {"\n"}  [endpoint]
                  </code>
                </div>
                <div className="quick-use-card">
                  <div className="quick-use-label">Python</div>
                  <code className="quick-use-code python">
                    import requests{"\n"}r = requests.post({"\n"}  url, json=payload,{"\n"}  headers=headers)
                  </code>
                </div>
                <div className="quick-use-card">
                  <div className="quick-use-label">JavaScript</div>
                  <code className="quick-use-code javascript">
                    await fetch(url, {"{"}
                    {"\n"}  method: &apos;POST&apos;,{"\n"}  body: JSON.stringify(p){"\n"}
                    {"}"})
                  </code>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ApiView;
